import { ColumnConfig } from 'components/NewBaseTable/NewBaseTable';
import { useTranslation } from 'react-i18next';
import { Modal, NewBaseTable } from 'components';
import { convertSecondsToTime, convertToZonedTimestamp } from 'common/helpers/dateAndTimeHelpersV2';
import ExternalLink from 'icons/indexed-icons/ExternalLink';
import { useMachineInfo } from 'common/pages/fleetV2/providers';
import CheckCircle from 'icons/indexed-icons/CheckCircle';
import { IcoError } from 'icons/indexed-icons/IcoError';
import { useState } from 'react';
import { useProcessLogDetailRunIdWidget } from '../SingleSessionSteps/utils/useProcessLogDetailAPIPovider';
import ProcessLogModal from '../SingleSessionSteps/ProcessLogModal';

interface SipNestedTableProps {
  data?: any;
}

export const SipNestedTable = ({ data }: SipNestedTableProps): JSX.Element => {
  const { t } = useTranslation(['mh']);
  const {  timeZone } = useMachineInfo() as { timeZone?: string };
  const [showModal, setShowModal] = useState<boolean>(false);
  const [runlogId, setRunlogId] = useState<string | null>(null);
  const { isLoading, processData, machineId } = useProcessLogDetailRunIdWidget(runlogId || '');

  const handleClick = (id: string) => {
    if (id) {
      setRunlogId(id);
      setShowModal(true);
    }
  };

  const generateColumnConfigsNewTable = (): ColumnConfig[] => {
    return [
      {
        id: 'sterilizationRunlogId',
        header: t('Run ID') as string,
        isEnableSorting: true,
        isSelected: true,
        renderer: (cellValue) => cellValue.getValue()
      },
      {
        id: 'startDatetimeUtc',
        header: t('Timestamp') as string,
        isEnableSorting: false,
        isSelected: true,
        renderer: ({ row }) => {
          return `${convertToZonedTimestamp(
            row?.original?.startDatetimeUtc as string,
            timeZone,
            'hh:mm:ss a'
          )} - ${convertToZonedTimestamp(
            row?.original?.endDatetimeUtc as string,
            timeZone,
            'hh:mm:ss a'
          )}`;
        }
      },
      {
        id: 'sterilizationResult',
        header: t('SIP Status') as string,
        isEnableSorting: true,
        isSelected: true,
        renderer: (cellValue) => (
          <div
            className={`sip-table-icon-box ${
              cellValue.getValue() !== 'Completed' ? 'sip-table-icon-error' : ''
            }`}
          >
            {cellValue.getValue() === 'Completed' ? <CheckCircle /> : <IcoError />}{' '}
            {cellValue.getValue()}
          </div>
        )
      },
      {
        id: 'sterilizationDuration',
        header: t('SIP total duration') as string,
        isEnableSorting: true,
        isSelected: true,
        renderer: (cellValue) => convertSecondsToTime(cellValue.getValue(), 'h:m')
      },
      {
        id: 'waitTimeDuration',
        header: t('Wait Time') as string,
        isEnableSorting: true,
        isSelected: true,
        renderer: (cellValue) => convertSecondsToTime(cellValue.getValue(), 'h:m')
      },
      {
        id: 'productionDuration',
        header: t('Production') as string,
        isEnableSorting: true,
        isSelected: true,
        renderer: (cellValue) => convertSecondsToTime(cellValue.getValue(), 'h:m')
      },
      {
        id: 'progressLogs',
        header: t('Progress Logs') as string,
        isEnableSorting: false,
        isSelected: true,
        renderer: ({ row }) => {
          const { sterilizationRunlogId: id } = row.original;
          return (
            <button className="button-issues" onClick={() => handleClick(id as string)}>
              <ExternalLink />
            </button>
          );
        }
      }
    ];
  };

  return (
    <div className="sip-nested-table">
      <NewBaseTable
        newTableData={data}
        columnConfigs={generateColumnConfigsNewTable()}
        customClassName="changeover-program-steps"
        textAlign="left"
        tdMarginLeft="0.625rem"
        sortState={{ id: 'name', desc: false }} // sort by name in descending order by default
        isShowColumnOptions={false}
        isShowGlobalSearch={false}
        disableVirtualize={true}
        hideTotalFetchCount={true}
      />
      <Modal visible={showModal} onClose={() => setShowModal(false)}>
        <ProcessLogModal
          active={showModal}
          handleClose={() => setShowModal(false)}
          isLoading={isLoading}
          processData={processData}
          processOrder={processData}
          machineId={machineId}
        />
      </Modal>
    </div>
  );
};
